// 用舊手機加入（MyCam 綁定碼的主手機那半，SPEC #mycam-bind、#mycam-v1 MV01 / MV03 / MV16）：
// 選家 → 開綁定碼（QR + 8 碼、30 分鐘）→ 舊手機掃 / 輸入 → 這裡顯示 4 位比對碼與舊手機型號 → 核對一樣才按「是，加入」。
// 跟觀看 App（home-base KinioMyCamBindDialog）同一組 API；離開頁面 = 作廢這張碼。
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { KinioError, kinioFetch, planeBase } from '@kinio/web-sdk';
import { useApp } from '../../lib/AppContext';
import { useScope } from '../../lib/scope';
import { PairQrCode } from '../devices/PairQrCode';

interface BindCode {
  bind_id: string;
  code: string;
  qr: string;
  expires_in_seconds: number;
}
interface BindStatus {
  status: 'waiting' | 'requested' | 'done' | 'expired' | 'cancelled';
  thing_id?: string;
  request?: { request_id: string; compare_code: string; device_label: string; platform: string };
}

type Phase = 'pick' | 'creating' | 'showing' | 'requested' | 'confirming' | 'done' | 'expired' | 'failed';

export function displayCode(code: string): string {
  return code.length === 8 ? `${code.slice(0, 4)} ${code.slice(4)}` : code;
}

export function MyCamAddPage() {
  const { t } = useTranslation();
  const { homes } = useScope();
  const { config } = useApp();
  // 這個品牌的家排前面：推播只送 App 品牌 = 家品牌（SPEC #mycam-v1 MV09，Eddy 2026-10-21 選單 A）
  const myBrand = config.brand.toLowerCase();
  const sameBrand = (b?: string) => !b || b.toLowerCase() === myBrand;
  const admins = useMemo(() => {
    const other = (b?: string) => Number(!!b && b.toLowerCase() !== myBrand);
    return homes.filter((h) => h.role === 'owner' || h.role === 'admin').sort((a, b) => other(a.brand) - other(b.brand));
  }, [homes, myBrand]);
  const [homeId, setHomeId] = useState<string>('');
  const home = admins.find((h) => h.home_id === homeId) ?? (admins.length === 1 ? admins[0] : undefined);
  const [phase, setPhase] = useState<Phase>('pick');
  const [code, setCode] = useState<BindCode | null>(null);
  const [req, setReq] = useState<BindStatus['request'] | null>(null);
  const [name, setName] = useState(t('mycam.name_default'));
  const [error, setError] = useState<string | null>(null);
  const [left, setLeft] = useState(0);
  const [run, setRun] = useState(0);
  const codeRef = useRef<{ base: string; bind: string } | null>(null);
  /** 選的家是別的品牌 App 建的：手機 App 收不到這台的移動通知 → 先說清楚，開始鈕改成〔仍加在這個家〕 */
  const brandMismatch = !!home && !sameBrand(home.brand);
  const base = home ? `${planeBase(home.endpoints.api)}/v1/homes/${encodeURIComponent(home.home_id)}/soft-devices/bind-codes` : '';

  // 離開頁面 = 作廢還沒用掉的碼
  useEffect(
    () => () => {
      const c = codeRef.current;
      if (c) void kinioFetch(`${c.base}/${encodeURIComponent(c.bind)}`, { method: 'DELETE' }).catch(() => undefined);
    },
    [],
  );

  useEffect(() => {
    if (!home || run === 0) return;
    let stop = false;
    (async () => {
      setPhase('creating');
      setError(null);
      setReq(null);
      let c: BindCode;
      try {
        c = await kinioFetch<BindCode>(base, { method: 'POST', body: {} });
      } catch (e) {
        if (!stop) {
          setError(e instanceof KinioError ? e.message : String(e));
          setPhase('failed');
        }
        return;
      }
      codeRef.current = { base, bind: c.bind_id };
      setCode(c);
      setPhase('showing');
      const end = Date.now() + c.expires_in_seconds * 1000;
      while (!stop && Date.now() < end) {
        setLeft(Math.max(0, Math.round((end - Date.now()) / 1000)));
        try {
          const st = await kinioFetch<BindStatus>(`${base}/${encodeURIComponent(c.bind_id)}`);
          if (st.status === 'requested' && st.request) {
            setReq(st.request);
            setPhase((p) => (p === 'showing' ? 'requested' : p));
          } else if (st.status === 'expired' || st.status === 'cancelled') {
            setPhase('expired');
            return;
          } else if (st.status === 'done') {
            return;
          }
        } catch {
          /* 網路一時斷：下一輪再問 */
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!stop) setPhase((p) => (p === 'showing' || p === 'requested' ? 'expired' : p));
    })();
    return () => {
      stop = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, base]);

  async function decide(yes: boolean) {
    if (!code || !req) return;
    setPhase('confirming');
    try {
      if (yes) {
        await kinioFetch(`${base}/${encodeURIComponent(code.bind_id)}/confirm`, { method: 'POST', body: { request_id: req.request_id, display_name: name.trim() || t('mycam.name_default') } });
        codeRef.current = null;
        setPhase('done');
      } else {
        await kinioFetch(`${base}/${encodeURIComponent(code.bind_id)}/reject`, { method: 'POST', body: { request_id: req.request_id } });
        setReq(null);
        setPhase('showing');
      }
    } catch (e) {
      const k = e instanceof KinioError ? e.code : '';
      if (k === 'BIND_REQUEST_GONE') {
        setReq(null);
        setPhase('showing');
      } else if (k === 'BIND_CODE_INVALID') {
        setPhase('expired');
      } else {
        setError(k === 'PLAN_LIMIT' ? t('mycam.plan_limit') : e instanceof KinioError ? e.message : String(e));
        setPhase('failed');
      }
    }
  }

  return (
    <div className="page mycam-add">
      <h1 className="page-title">{t('mycam.add_title')}</h1>
      {admins.length === 0 ? (
        <p className="muted">{t('mycam.no_admin_home')}</p>
      ) : phase === 'pick' ? (
        <div className="stack">
          <p>{t('mycam.add_intro')}</p>
          {admins.length > 1 && (
            <label className="field">
              <span>{t('mycam.home')}</span>
              <select className="input" value={home?.home_id ?? ''} onChange={(e) => setHomeId(e.target.value)}>
                <option value="" disabled>
                  {t('mycam.pick_home')}
                </option>
                {admins.map((h) => (
                  <option key={h.home_id} value={h.home_id}>
                    {h.display_name || h.home_id}
                  </option>
                ))}
              </select>
            </label>
          )}
          {brandMismatch && (
            <p className="notice" role="note" data-testid="mycam-brand-warn">
              {t('mycam.brand_warn', { home: home?.display_name || home?.home_id })}
            </p>
          )}
          <button className="btn btn-primary" disabled={!home} onClick={() => setRun((n) => n + 1)} data-testid="mycam-add-start">
            {brandMismatch ? t('mycam.brand_keep') : t('mycam.create_code')}
          </button>
        </div>
      ) : (
        <div className="stack" aria-live="polite">
          {(phase === 'creating' || phase === 'confirming') && <p className="muted">{t('common.loading')}</p>}
          {phase === 'showing' && code && (
            <>
              <p>{t('mycam.steps')}</p>
              <PairQrCode text={code.qr} label={t('mycam.qr_label')} />
              <p className="mycam-code" data-testid="mycam-code" aria-label={code.code.split('').join(' ')}>
                {displayCode(code.code)}
              </p>
              <p className="muted">{t('mycam.expires', { n: Math.ceil(left / 60) })}</p>
              <p className="muted">{t('mycam.waiting')}</p>
            </>
          )}
          {phase === 'requested' && req && (
            <>
              <h2>{t('mycam.compare_title')}</h2>
              <p className="mycam-compare" data-testid="mycam-compare" aria-label={req.compare_code.split('').join(' ')}>
                {req.compare_code}
              </p>
              {req.device_label && <p className="muted">{t('mycam.compare_device', { device: req.device_label })}</p>}
              <label className="field">
                <span>{t('mycam.name')}</span>
                <input className="input" value={name} maxLength={64} onChange={(e) => setName(e.target.value)} />
              </label>
              <div className="row">
                <button className="btn btn-primary" onClick={() => void decide(true)} data-testid="mycam-confirm">
                  {t('mycam.confirm')}
                </button>
                <button className="btn" onClick={() => void decide(false)}>
                  {t('mycam.reject')}
                </button>
              </div>
            </>
          )}
          {phase === 'done' && (
            <>
              <p role="status">{t('mycam.done', { name: name.trim() || t('mycam.name_default') })}</p>
              <Link className="btn btn-primary" to="/live">
                {t('mycam.go_cameras')}
              </Link>
            </>
          )}
          {phase === 'expired' && (
            <>
              <p>{t('mycam.expired')}</p>
              <button className="btn btn-primary" onClick={() => setRun((n) => n + 1)}>
                {t('mycam.create_code')}
              </button>
            </>
          )}
          {phase === 'failed' && (
            <>
              <p role="alert" className="error">
                {error}
              </p>
              <button className="btn btn-primary" onClick={() => setRun((n) => n + 1)}>
                {t('mycam.create_code')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
